import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { useVehiculesStore } from "@store/vehiculesStore";
import { useMessagerieStore } from "@store/messagerieStore";
import { useTagStore } from "@store/tagStore";
import InternalBookingPage from "@components/InternalBookingPage";
import { useNavigate } from "react-router-dom";
import { paymentsApi } from "@services/api/payments";
import type { Payment } from "types/payment";
import { recallCampaignsApi, type RecallCampaignGroup } from "@services/api/recallCampaigns";
import type { WidgetConfig, WidgetId, WidgetSize } from "types/dashboard";
import { getDefaultLayout } from "@components/dashboard/widgetRegistry";
import WidgetRenderer from "@components/dashboard/WidgetRenderer";
import DashboardConfigModal from "@components/dashboard/DashboardConfigModal";

// localStorage key for dashboard layout (instant persistence, no backend dependency)
const LAYOUT_STORAGE_KEY = "heric_dashboard_layout";

function loadLayout(): WidgetConfig[] | null {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function saveLayout(widgets: WidgetConfig[]) {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(widgets));
  } catch {}
}

// Un widget ajouté après coup n'apparaîtrait pour personne : toutes les mises en
// page déjà enregistrées l'ignorent. On l'insère donc une seule fois par
// utilisateur — la trace en localStorage évite de le faire revenir si l'on
// choisit ensuite de le retirer dans « Personnaliser ».
const MIGRATIONS_STORAGE_KEY = "heric_dashboard_widget_migrations";

const AUTO_ADDED_WIDGETS: { id: WidgetId; size: WidgetSize; index: number }[] = [
  { id: "messages_non_lus", size: "small", index: 1 },
];

function applyWidgetMigrations(widgets: WidgetConfig[]): WidgetConfig[] {
  let applied: string[] = [];
  try {
    applied = JSON.parse(localStorage.getItem(MIGRATIONS_STORAGE_KEY) || "[]");
  } catch {}

  const next = [...widgets].sort((a, b) => a.position - b.position);
  const newlyApplied: string[] = [];
  for (const widget of AUTO_ADDED_WIDGETS) {
    if (applied.includes(widget.id)) continue;
    newlyApplied.push(widget.id);
    if (next.some((w) => w.id === widget.id)) continue;
    next.splice(Math.min(widget.index, next.length), 0, {
      id: widget.id,
      position: 0,
      size: widget.size,
    });
  }
  if (newlyApplied.length === 0) return widgets;

  try {
    localStorage.setItem(
      MIGRATIONS_STORAGE_KEY,
      JSON.stringify([...applied, ...newlyApplied]),
    );
  } catch {}
  return next.map((w, i) => ({ ...w, position: i }));
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const updateUserData = useDataStore((state) => state.updateUserData);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  // Fetch data on mount
  const fetchOrdres = useOrdreStore((s) => s.fetchOrdres);
  const fetchVehicles = useVehiculesStore((s) => s.fetchVehicles);
  const initGlobal = useMessagerieStore((s) => s.initGlobal);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [campaigns, setCampaigns] = useState<RecallCampaignGroup[]>([]);

  useEffect(() => {
    if (orgData?.id) {
      fetchOrdres(orgData.id);
      fetchVehicles(orgData.id);
      useTagStore.getState().fetchTags(orgData.id);
      paymentsApi.listAll().then(setPayments).catch(() => {});
      recallCampaignsApi.listGrouped(orgData.id).then(setCampaigns).catch(() => {});
    }
    initGlobal();
  }, [orgData?.id]);

  // Dashboard layout: localStorage first, then user config, then default
  const [dashboardWidgets, setDashboardWidgets] = useState<WidgetConfig[]>(
    () => applyWidgetMigrations(
      loadLayout()
        || (userData?.config as any)?.dashboard_layout?.widgets
        || getDefaultLayout()
    )
  );

  const handleSaveLayout = useCallback(
    (widgets: WidgetConfig[]) => {
      setDashboardWidgets(widgets);
      saveLayout(widgets);
      setConfigOpen(false);

      // Also persist to backend (fire-and-forget)
      if (userData?.id) {
        updateUserData(userData.id, {
          config: {
            ...userData.config,
            dashboard_layout: { widgets },
          },
        }).catch(() => {});
      }
    },
    [userData, updateUserData],
  );

  // Drag-and-drop state for reordering widgets on the dashboard
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleWidgetDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleWidgetDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndexRef.current !== null && dragIndexRef.current !== index) {
      setDragOverIndex(index);
    }
  };

  const handleWidgetDrop = (toIndex: number) => {
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === toIndex) {
      dragIndexRef.current = null;
      setDragOverIndex(null);
      return;
    }
    const next = [...dashboardWidgets].sort((a, b) => a.position - b.position);
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    const reindexed = next.map((w, i) => ({ ...w, position: i }));
    handleSaveLayout(reindexed);
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleWidgetDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  // Split widgets into full-width and 2-column layout
  const sorted = useMemo(
    () => [...dashboardWidgets].sort((a, b) => a.position - b.position),
    [dashboardWidgets],
  );

  const sharedData = useMemo(
    () => ({ payments, campaigns }),
    [payments, campaigns],
  );

  return (
    <div className="flex flex-col overflow-auto max-h-screen relative">
      <div className="flex flex-col h-fit pb-96 pt-10 px-4 lg:px-10">
        <div className="flex flex-col w-full max-w-1p2k gap-6">
          {/* Header */}
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col">
              <Text1>{`Bonjour${userData?.infos.display_name ? `, ${userData.infos.display_name}` : ""}`}</Text1>
              <Text2>Voici un aperçu de votre activité.</Text2>
            </div>
            <button
              onClick={() => setConfigOpen(true)}
              className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg border hover:bg-neutral-50 transition-colors"
            >
              <Icon1 name="settings" size={16} color="#999" />
              <span className="text-h3 text-neutral-500">Personnaliser</span>
            </button>
          </div>

          {/* Full-width widgets */}
          {sorted.filter((w) => w.size === "large").map((w) => {
            const globalIdx = sorted.indexOf(w);
            return (
              <div
                key={w.id}
                className={`transition-all${dragOverIndex === globalIdx ? " ring-2 ring-indigo-300 rounded-xl" : ""}`}
                draggable
                onDragStart={() => handleWidgetDragStart(globalIdx)}
                onDragOver={(e) => handleWidgetDragOver(e, globalIdx)}
                onDrop={() => handleWidgetDrop(globalIdx)}
                onDragEnd={handleWidgetDragEnd}
                style={{ cursor: "grab" }}
              >
                <WidgetRenderer id={w.id} data={sharedData} />
              </div>
            );
          })}

          {/* Two-column layout */}
          {sorted.some((w) => w.size !== "large") && (
            <div className="flex flex-col lg:flex-row gap-4">
              {[0, 1].map((col) => (
                <div key={col} className="flex flex-col flex-1 gap-4">
                  {sorted
                    .filter((w) => w.size !== "large")
                    .filter((_, i) => i % 2 === col)
                    .map((w) => {
                      const globalIdx = sorted.indexOf(w);
                      return (
                        <div
                          key={w.id}
                          className={`transition-all${dragOverIndex === globalIdx ? " ring-2 ring-indigo-300 rounded-xl" : ""}`}
                          draggable
                          onDragStart={() => handleWidgetDragStart(globalIdx)}
                          onDragOver={(e) => handleWidgetDragOver(e, globalIdx)}
                          onDrop={() => handleWidgetDrop(globalIdx)}
                          onDragEnd={handleWidgetDragEnd}
                          style={{ cursor: "grab" }}
                        >
                          <WidgetRenderer id={w.id} data={sharedData} />
                        </div>
                      );
                    })}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Config modal */}
      {configOpen && (
        <DashboardConfigModal
          widgets={dashboardWidgets}
          onSave={handleSaveLayout}
          onClose={() => setConfigOpen(false)}
        />
      )}

      {/* Floating widget button + overlay */}
      {widgetOpen ? (
        <div className="fixed bottom-6 right-6 z-50">
          <InternalBookingPage onClose={() => setWidgetOpen(false)} onSuccess={() => {
            if (orgData?.id) {
              useOrdreStore.setState({ isRefreshing: false });
              useVehiculesStore.setState({ isRefreshing: false });
              fetchOrdres(orgData.id);
              fetchVehicles(orgData.id);
            }
          }} />
        </div>
      ) : (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2">
          <button
            onClick={() => navigate("/booking-debug")}
            className="flex items-center gap-2 rounded-full px-5 py-3 shadow-xl hover:scale-105 transition-transform"
            style={{ background: "#5C6BFF" }}
          >
            <Icon1 name="search" size={20} color="#FFFFFF" />
            <span className="text-h2 font-medium text-white">Debug Dispo</span>
          </button>
          <button
            onClick={() => window.open("https://broad-vacation-657606.framer.app/", "_blank")}
            className="flex items-center gap-2 rounded-full px-5 py-3 shadow-xl hover:scale-105 transition-transform"
            style={{ background: "#000000" }}
          >
            <Icon1 name="open" size={20} color="#FFFFFF" />
            <span className="text-h2 font-medium text-white">Voir le site</span>
          </button>
        </div>
      )}
    </div>
  );
}
